/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { getAxios } from '@/lib/axios';
import { requestBaseURL } from '@/config/serviceAPI';
import { type AxiosInstance } from 'axios';
import { type QueryClient } from '@tanstack/react-query';
import type { ReviewCandidatesOptions, ReviewCandidatesResponse } from '../interface/types';
import { isRetryableError } from '../utils/retryStrategies';
import { API_URLS } from '@/constants/apiUrls';
import {
  type BaseInfiniteMergeFunction,
  type BaseMergedData,
  createInfiniteQuery,
} from '@/utils/query/createInfiniteQuery';

const URL = `${requestBaseURL}${API_URLS.REVIEW_CANDIDATES}`;

const getReviewCandidates = async (
  options?: ReviewCandidatesOptions,
  page?: number,
  axiosInstance?: AxiosInstance,
  CUSTOM_URL?: string,
) => {
  const { limit } = options ?? {};
  const params = { ...(limit ? { limit: String(limit) } : undefined), page: String(page ?? 1) };
  const response = await getAxios(axiosInstance).get<ReviewCandidatesResponse>(
    `${CUSTOM_URL || URL}`,
    { params },
  );
  return response.data;
};

const selectInfiniteQueryData: BaseInfiniteMergeFunction<ReviewCandidatesResponse, number> = (
  data,
) => {
  const mergedData = data.pages.reduce(
    (acc: ReviewCandidatesResponse, page: ReviewCandidatesResponse) => {
      return {
        ...acc,
        ...page,
        productReviewCandidates: [...acc.productReviewCandidates, ...page.productReviewCandidates],
      };
    },
    { totalPages: 0, currentPage: 1, productReviewCandidates: [] } as ReviewCandidatesResponse,
  );

  return {
    mergedData,
    pages: data.pages,
    pageParams: data.pageParams,
    pageNumber: data.pages.length,
  };
};

const {
  useData: useReviewCandidatesData,
  prefetch: prefetchReviewCandidatesData,
  query: reviewCandidatesQuery,
} = createInfiniteQuery<
  ReviewCandidatesResponse,
  ReviewCandidatesOptions | undefined,
  number,
  BaseMergedData<ReviewCandidatesResponse, number>
>(['reviewCandidates'], getReviewCandidates, {
  initialPageParam: 1,
  getNextPageParam: (lastPage) => {
    if (lastPage.currentPage < lastPage.totalPages) {
      return lastPage.currentPage + 1;
    }
    return undefined;
  },
  selectInfiniteQueryData,
});

const useReviewCandidates = (options?: ReviewCandidatesOptions) => {
  const { enabled = true, ...rest } = options ?? {};

  const infiniteQueryResult = useReviewCandidatesData({
    queryOptions: rest,
    enabled,
    retry: isRetryableError,
    retryDelay: 1000,
  });

  const {
    data: infiniteData,
    fetchNextPage,
    hasNextPage,
    fetchPreviousPage,
    hasPreviousPage,
    isFetching,
    isPending,
    isError,
    status,
    refetch,
    isFetchingNextPage,
  } = infiniteQueryResult;

  const { mergedData, pages, pageNumber = 1 } = infiniteData ?? {};

  return {
    pages,
    data: mergedData,
    pageNumber,
    fetchNextPage,
    hasNextPage,
    fetchPreviousPage,
    hasPreviousPage,
    isFetching,
    isPending,
    isError,
    status,
    refetch,
    isFetchingNextPage,
  };
};

const prefetchReviewCandidates = (
  queryClient: QueryClient,
  axiosInstance: AxiosInstance,
  options?: ReviewCandidatesOptions,
) => {
  return prefetchReviewCandidatesData(queryClient, options ?? {}, axiosInstance);
};

export { useReviewCandidates, prefetchReviewCandidates, reviewCandidatesQuery };
